<template>
	<v-container fluid class="pa-4">
		<v-card class="background-2 round-card">
			<v-card-title class="pa-3">
				<p class="pl-2 pl-xl-3 mb-0">Dichiarazione di Applicabilità (SoA) — ISO 27001:2022</p>
				<v-spacer></v-spacer>
				<v-btn v-if="items.length" text color="info" class="mr-2" :loading="pdfLoading" @click="downloadPdf">
					<v-icon left>mdi-file-pdf-box</v-icon> Esporta PDF
				</v-btn>
				<v-btn v-if="items.length === 0" color="primary" :loading="initializing" @click="initialize">
					<v-icon left>mdi-playlist-plus</v-icon> Inizializza SoA (93 controlli)
				</v-btn>
			</v-card-title>
			<v-divider></v-divider>

			<!-- Riepilogo -->
			<v-card-text v-if="items.length" class="pa-4">
				<v-row>
					<v-col cols="6" md="3"><div class="grey--text">Applicabili</div><div class="text-h6">{{ summary.applicable }}/{{ summary.total }}</div></v-col>
					<v-col cols="6" md="3"><div class="grey--text">Implementati</div><div class="text-h6 green--text">{{ summary.implemented }}</div></v-col>
					<v-col cols="6" md="3"><div class="grey--text">Parziali</div><div class="text-h6 orange--text">{{ summary.partial }}</div></v-col>
					<v-col cols="6" md="3"><div class="grey--text">Non implementati</div><div class="text-h6 red--text">{{ summary.not_implemented }}</div></v-col>
				</v-row>
				<v-progress-linear class="mt-2" :value="implementedPct" color="success" height="8" rounded></v-progress-linear>
			</v-card-text>

			<v-divider v-if="items.length"></v-divider>

			<!-- Filtro tema -->
			<v-card-text v-if="items.length" class="pa-3">
				<v-chip-group v-model="themeFilter" column>
					<v-chip filter value="" small>Tutti</v-chip>
					<v-chip filter value="organizational" small>Organizzativi</v-chip>
					<v-chip filter value="people" small>Persone</v-chip>
					<v-chip filter value="physical" small>Fisici</v-chip>
					<v-chip filter value="technological" small>Tecnologici</v-chip>
				</v-chip-group>
			</v-card-text>

			<v-simple-table v-if="items.length" class="background-2">
				<thead>
					<tr>
						<th style="width:110px">Controllo</th>
						<th>Titolo</th>
						<th style="width:110px">Applicabile</th>
						<th style="width:200px">Stato</th>
						<th>Giustificazione / Note</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="item in filteredItems" :key="item.id">
						<td class="font-weight-medium">{{ item.clause }}</td>
						<td>{{ item.title }}</td>
						<td>
							<v-switch v-model="item.applicable" inset dense hide-details class="mt-0 pt-0" @change="save(item)"></v-switch>
						</td>
						<td>
							<v-select v-model="item.implementation_status" :items="statusOptions" item-text="text" item-value="value"
								outlined dense hide-details class="my-1" :menu-props="{'offset-y': true}" :disabled="!item.applicable" @change="save(item)"></v-select>
						</td>
						<td>
							<v-text-field v-model="item.justification" outlined dense hide-details class="my-1" placeholder="Motivazione..." @blur="save(item)"></v-text-field>
						</td>
					</tr>
				</tbody>
			</v-simple-table>

			<v-card-text v-if="!loading && items.length === 0" class="pa-6 text-center grey--text">
				Nessuna voce SoA. Clicca "Inizializza SoA" per generare le righe dai 93 controlli Annex A.
			</v-card-text>
		</v-card>
	</v-container>
</template>

<script>
export default {
	name: 'ComplianceSoaMatrix',
	data() {
		return {
			loading: true,
			initializing: false,
			pdfLoading: false,
			items: [],
			summary: {},
			themeFilter: '',
			statusOptions: [
				{ value: 'non_implementato', text: 'Non implementato' },
				{ value: 'parziale', text: 'Parziale' },
				{ value: 'implementato', text: 'Implementato' }
			]
		}
	},
	computed: {
		filteredItems() {
			if (!this.themeFilter) return this.items
			return this.items.filter(i => i.theme === this.themeFilter)
		},
		implementedPct() {
			if (!this.summary.applicable) return 0
			return Math.round((this.summary.implemented / this.summary.applicable) * 100)
		}
	},
	created() { this.load() },
	methods: {
		downloadPdf() {
			this.pdfLoading = true
			this.axios.get('compliancesoa/export-pdf', { responseType: 'blob' }).then(r => {
				this.pdfLoading = false
				const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
				const a = document.createElement('a')
				a.href = url
				a.download = 'SoA.pdf'
				document.body.appendChild(a)
				a.click()
				a.remove()
				window.URL.revokeObjectURL(url)
			}, () => { this.pdfLoading = false; this.$store.commit('setSnackbarError') })
		},
		load() {
			this.loading = true
			this.axios.post('compliancesoa-filters', {}).then(r => {
				this.items = r.data.data
				this.summary = r.data.summary
				this.loading = false
			}, () => { this.loading = false; this.$store.commit('setSnackbarError') })
		},
		initialize() {
			this.initializing = true
			this.axios.post('compliancesoa/initialize').then(r => {
				this.initializing = false
				this.$store.commit('setSnackbar', { value: true, color: 'success', text: 'Generati ' + r.data.created + ' controlli', timeout: 4000 })
				this.load()
			}, error => {
				this.initializing = false
				const msg = error.response && error.response.data ? error.response.data.message : 'Errore'
				this.$store.commit('setSnackbar', { value: true, color: 'error', text: msg, timeout: 6000 })
			})
		},
		save(item) {
			this.axios.put('compliancesoa/' + item.id, {
				applicable: item.applicable,
				implementation_status: item.implementation_status,
				justification: item.justification,
				notes: item.notes
			}).then(() => {
				// aggiorna riepilogo senza ricaricare tutto
				this.refreshSummary()
			}, () => this.$store.commit('setSnackbarError'))
		},
		refreshSummary() {
			this.summary = {
				total: this.items.length,
				applicable: this.items.filter(i => i.applicable).length,
				implemented: this.items.filter(i => i.implementation_status === 'implementato').length,
				partial: this.items.filter(i => i.implementation_status === 'parziale').length,
				not_implemented: this.items.filter(i => i.implementation_status === 'non_implementato').length
			}
		}
	}
}
</script>
